import { useMemo } from "react";
import type { DerivedGallery, InvocationPhoto } from "../types.js";
import { formatInstant, photoRoleLabel } from "../lib/format.js";
import { derivedPhotoFor } from "../lib/pipeline.js";

/**
 * The left rail (handoff §2 / brief §2 / §5.1). A panel shoot produces two
 * *tracks* of photo that take different processing paths, and the single
 * most important thing a support engineer needs to see first is which track
 * a photo went down — a bad result is diagnosed completely differently
 * depending on the track:
 *
 *  - **Identity track** — `legendCard` / `deadFrontOn` (+ untagged label
 *    shots). Read whole, no pre-processing; answers "what panel is this?".
 *  - **Read track** — `breakerCloseUp`. Rectified into one whole-panel read
 *    image and read in a single pass; answers "what are the breakers?".
 *
 * The split is structural — membership in the derived read manifest — so it
 * stays correct even when capture roles weren't tagged.
 *
 * UR-6 — the union read path no longer tiles, so the rail's old Tiles
 * thumbnail grid is gone. The new scan process also has no separate geometry
 * pass, so the per-stage debug cards are gone too. The center pane shows the
 * read image directly; the rail carries only the photo tracks.
 */

export type PhotosRailProps = {
  readonly photos: readonly InvocationPhoto[];
  readonly derived: DerivedGallery | null;
  readonly breakerPhotoIds: ReadonlySet<string>;
  readonly selectedPhotoId: string | null;
  readonly onSelectPhoto: (photoId: string) => void;
};

export function PhotosRail({
  photos,
  derived,
  breakerPhotoIds,
  selectedPhotoId,
  onSelectPhoto,
}: PhotosRailProps) {
  const identityPhotos = photos.filter((p) => !breakerPhotoIds.has(p.photoId));
  const readPhotos = photos.filter((p) => breakerPhotoIds.has(p.photoId));
  const photoSelected = (photoId: string) => photoId === selectedPhotoId;

  return (
    <nav
      aria-label="photos-rail"
      className="flex h-full flex-col gap-5 overflow-y-auto border-r border-border-1 bg-bg-2 p-4"
    >
      {photos.length === 0 ? (
        <p className="px-1 py-2 text-sm text-fg-3">
          No photos were submitted with this invocation.
        </p>
      ) : null}

      {identityPhotos.length > 0 ? (
        <section aria-label="track Identity track" className="flex flex-col gap-2.5">
          <SectionHeader title="Identity track" hint="read whole" accent={false} />
          <div className="flex flex-col gap-2">
            {identityPhotos.map((photo) => (
              <PhotoCard
                key={photo.photoId}
                photo={photo}
                derived={derived}
                selected={photoSelected(photo.photoId)}
                onSelect={onSelectPhoto}
              />
            ))}
          </div>
        </section>
      ) : null}

      {readPhotos.length > 0 ? (
        <section aria-label="track Read track" className="flex flex-col gap-2.5">
          <SectionHeader title="Read track" hint="read image" accent />
          <div className="flex flex-col gap-2">
            {readPhotos.map((photo) => (
              <PhotoCard
                key={photo.photoId}
                photo={photo}
                derived={derived}
                selected={photoSelected(photo.photoId)}
                onSelect={onSelectPhoto}
              />
            ))}
          </div>
        </section>
      ) : null}
    </nav>
  );
}

function SectionHeader({
  title,
  hint,
  accent,
}: {
  readonly title: string;
  readonly hint: string;
  readonly accent: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className={`text-xs font-semibold uppercase tracking-[0.08em] ${
          accent ? "text-accent" : "text-fg-2"
        }`}
      >
        {title}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-fg-3">
        {hint}
      </span>
    </div>
  );
}

function PhotoCard({
  photo,
  derived,
  selected,
  onSelect,
}: {
  readonly photo: InvocationPhoto;
  readonly derived: DerivedGallery | null;
  readonly selected: boolean;
  readonly onSelect: (photoId: string) => void;
}) {
  // For a read-track card, summarise its read image: the sent dimensions and
  // how many geometry columns the union spanned (the diagnostic shape).
  const subline = useMemo(() => {
    const dp = derivedPhotoFor(derived, photo.photoId);
    const read = dp?.read ?? null;
    if (read === null) return "read whole";
    const cols = read.columnBoxes.length;
    return `${read.sentWidth}×${read.sentHeight} · ${cols} col${cols === 1 ? "" : "s"}${read.lowRes ? " · low-res" : ""}`;
  }, [derived, photo.photoId]);

  const untagged = photo.role === undefined;

  return (
    <button
      type="button"
      aria-label={`photo ${photo.photoId}`}
      aria-current={selected}
      onClick={() => onSelect(photo.photoId)}
      className={`flex w-full gap-3 rounded-lg border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        selected
          ? "border-accent bg-accent-soft"
          : "border-border-1 bg-bg-3 hover:bg-bg-4"
      }`}
    >
      <img
        src={photo.url}
        alt={`Thumbnail of ${photo.photoId}`}
        className="h-[60px] w-[46px] flex-none rounded-md border border-line-strong bg-bg-0 object-cover"
      />
      <span className="flex min-w-0 flex-col gap-1 py-0.5">
        <span
          className="truncate text-[13px] font-semibold text-fg-1"
          title={photo.photoId}
        >
          {photo.photoId}
        </span>
        {untagged ? (
          <span className="w-fit rounded-sm bg-warning-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
            No role
          </span>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-wide text-fg-3">
            {photoRoleLabel(photo.role)}
          </span>
        )}
        <span className="text-[11px] text-fg-3">{subline}</span>
        {photo.capturedAt !== undefined ? (
          // KAN-83 — when the photo was taken. For a gallery import this is
          // the source's real EXIF capture time, which can predate the
          // upload; showing it stops support from misreading the import
          // moment as the capture moment.
          <span className="text-[11px] text-fg-3" title="Capture time (UTC)">
            {formatInstant(photo.capturedAt)}
          </span>
        ) : null}
        {photo.captureDevice !== undefined ? (
          // KAN-63 — the device that captured this photo (make/model),
          // surfaced so support can correlate a result with the hardware.
          <span
            className="truncate text-[11px] text-fg-3"
            title={`Captured on ${photo.captureDevice}`}
          >
            {photo.captureDevice}
          </span>
        ) : null}
      </span>
    </button>
  );
}
